import { type LiveValuesTransform } from '@framework/validation/live/LiveTypes.ts';
import { get, isEqual, set } from 'lodash';
import { type IEngineSemanticTag } from '../form-builder/semanticTags/IEngineSemanticTag.ts';
import { isUniqueSemanticTag } from '../form-builder/semanticTags/isUniqueSemanticTag.ts';

interface ILiveContextWithSemanticTags {
  semanticTags?: IEngineSemanticTag[] | null;
}

interface ISemanticTagSyncParams {
  pathsBySemanticTagId: Map<string, string[]>;
  runtimeUniqueSemanticTagIds: Set<string>;
  changedPath: string;
}

/**
 * Costruisce la transform che allinea i valori tra widget che esportano lo
 * stesso semantic tag univoco.
 *
 * La scansione widget fornisce `semanticTagId -> rhfPath[]`. Il catalogo
 * runtime (`context.semanticTags`) decide quali tag sono davvero univoci.
 */
export function createSemanticTagSyncValuesTransform(
  pathsBySemanticTagId: Map<string, string[]>,
): LiveValuesTransform {
  return ({ values, context, event }) => {
    // Senza path dell'evento non sappiamo quale campo ha innescato la sync.
    const changedPath = event?.path;
    if (!changedPath || pathsBySemanticTagId.size === 0) {
      return { values, changedPaths: [] };
    }

    // La mappa dei path dice solo dove compare lo stesso tag.
    // Il catalogo runtime decide se quel tag va sincronizzato oppure no.
    const siblingPathsToSync = getSemanticTagSiblingPathsToSync({
      pathsBySemanticTagId,
      runtimeUniqueSemanticTagIds: getUniqueSemanticTagIds(context),
      changedPath,
    });
    const changedPaths: string[] = [];

    // Aggiorna solo i campi davvero diversi: evita patch live inutili.
    for (const path of siblingPathsToSync) {
      const currentValue = get(values, path);

      if (!isEqual(currentValue, event.value)) {
        set(values, path, event.value);
        changedPaths.push(path);
      }
    }

    return { values, changedPaths };
  };
}

/**
 * Restituisce i path da aggiornare quando cambia un campo con semantic tag.
 *
 * Cerca il gruppo `semanticTagId -> rhfPath[]` che contiene il path cambiato.
 * Se il tag è univoco nel catalogo runtime, ritorna tutti gli altri path del
 * gruppo. Se il tag non è univoco, non sincronizza nulla.
 */
function getSemanticTagSiblingPathsToSync({
  pathsBySemanticTagId,
  runtimeUniqueSemanticTagIds,
  changedPath,
}: ISemanticTagSyncParams): string[] {
  for (const [semanticTagId, paths] of pathsBySemanticTagId) {
    // Se il path modificato appartiene a un tag univoco, gli altri path
    // dello stesso gruppo devono ricevere lo stesso valore.
    const changedPathBelongsToUniqueTag =
      runtimeUniqueSemanticTagIds.has(semanticTagId) &&
      paths.includes(changedPath);

    if (changedPathBelongsToUniqueTag) {
      return paths.filter(path => path !== changedPath);
    }
  }

  return [];
}

/**
 * Estrae dal context runtime gli id dei semantic tag configurati come univoci.
 *
 * La scansione widget sa solo quali campi dichiarano lo stesso tag. Questa
 * funzione applica la regola di dominio: solo i tag univoci devono propagare
 * il valore tra più widget.
 */
function getUniqueSemanticTagIds(
  context: ILiveContextWithSemanticTags | null | undefined,
): Set<string> {
  const tags = context?.semanticTags;
  if (!Array.isArray(tags)) return new Set();

  const uniqueSemanticTagIds = new Set<string>();

  for (const tag of tags) {
    if (tag.id && isUniqueSemanticTag(tag)) {
      uniqueSemanticTagIds.add(tag.id);
    }
  }

  return uniqueSemanticTagIds;
}
